---
title: 'Does Your Chatbot Need an AI Disclosure? A Decision Guide'
description: 'Article 50 requires telling people when they are talking to an AI, though not in every case. The four tests that decide whether your chatbot needs a disclosure, and what actually counts as one.'
slug: 'chatbot-ai-disclosure-requirements'
publishedAt: '2026-08-17'
author: 'Syrcha'
tags: ['EU AI Act', 'Chatbots', 'Transparency']
readingMinutes: 6
draft: false
---

Since Article 50 of the EU AI Act took effect on 2 August 2026, one question comes up more than any other: does our support chatbot need a disclosure?

Usually yes. Teams who decide otherwise are normally leaning on an exemption that's much narrower than it sounds. Here are four tests to work through, then what a compliant disclosure actually looks like.

For the wider picture, see our guide to [what Article 50 requires of websites](/blog/eu-ai-act-article-50-website-requirements).

## The four tests

All four have to be true **at the same time**. Miss any one and you're out of scope.

### Test 1: Is it an AI system?

Not every automated interface qualifies. A decision-tree bot following fixed, hand-written rules (press 1 for billing, press 2 for support) generally isn't an AI system in the regulation's sense. A model that generates responses, classifies intent, or adapts its output is.

The practical line: were the responses written in advance and selected by fixed logic, or is a model producing the words? First case, usually outside. Second case, inside.

### Test 2: Does it involve genuine two-way exchange?

The obligation targets _interaction_: a system where a person sends something and gets a response back. A recommendation carousel, an autocomplete suggestion, a spam filter running silently in the background: none of these qualify, because nobody's having an exchange with them.

### Test 3: Is the interaction direct?

The AI has to be communicating with the person, not feeding suggestions to a human who then decides what to say. This one catches teams out in both directions.

An AI drafting replies that a support agent reviews and sends is **not** direct, because the human is the one communicating. A bot that answers autonomously and escalates only when it gets stuck **is** direct for the part it handles itself.

If you run a hybrid model, the handover point matters. The AI-handled portion needs disclosure.

### Test 4: Is the other party a natural person?

A person, not a business system. A chatbot answering customers is in scope. A machine-to-machine API with no human at the other end isn't.

## The exemption everyone over-relies on

You don't need a disclosure where it's already obvious the user is dealing with an AI. Sounds generous. In practice it almost never helps.

The standard is what would be obvious to a **reasonably well-informed, observant and circumspect** person. That's an objective test, measured against an ordinary member of your audience, not against you or a technically literate early adopter.

What doesn't clear that bar on its own:

- Naming the bot "Assistant", "Aria" or "Bot". Plenty of humans work under first names in support roles.
- A robot avatar or icon. Decorative, easily missed, and meaningless to a screen-reader user.
- A line buried in your terms of service or privacy policy. Nobody reads those mid-conversation, and the disclosure has to be perceivable in the interaction itself.
- Instant responses. Fast human agents exist.

The exemption is aimed at cases where context makes it unmistakable. A tool explicitly branded and presented as an AI product, where a user couldn't plausibly think otherwise.

If you're debating whether your case qualifies, add the disclosure. It costs one line of text.

## When it has to appear

At the latest, at the point of first interaction. In practice the user should know before they type anything meaningful, not after they've explained their whole problem to what they assumed was a person.

The chat window header works. So does an opening message shown before the input is enabled.

## What a good disclosure looks like

Clear, plain language, perceivable in the interface itself. Some examples that work:

```text
You are chatting with an AI assistant. Ask to speak to a human at any time.
```

```text
Hi, I am an AI assistant. I can help with orders, returns and account
questions, and I will pass you to a colleague if you need one.
```

```text
This conversation is handled by an automated AI system.
```

Three things make a real difference. Put it in the visible interface, not only in the first bubble that scrolls away, because a persistent line in the header survives a long conversation. Make it available to assistive technology, because a disclosure that exists only as an image, or that a screen reader never announces, hasn't been communicated to everyone. And say what happens next: telling people how to reach a human isn't required by Article 50, but it turns a legal notice into something useful.

## Special cases worth checking

**Voice agents.** Same logic, but the disclosure has to be audible. A written notice on a web page doesn't cover a phone call.

**Third-party widgets.** Embed a vendor's chat product and you're the deployer. Check what the widget displays by default, because you inherit the exposure even though you didn't build it.

**Handover to a human.** Where a conversation moves from AI to a person, make the transition visible. Users shouldn't have to guess which one they're talking to.

**Internal tools.** Article 50 covers professional use, not purely personal use. A bot your staff use is still deployed in a professional context, so don't assume internal means exempt.

## In short

Work through the four tests. If your bot is model-driven, has a real back-and-forth, talks to people directly, and those people are humans, you need a disclosure. The "it's obvious" exemption almost certainly won't save you.

The obligation itself is small: one line of text, visible before the first exchange, in the interface and not in a policy document.

---

_This article is general information, not legal advice. If your system does anything more complex than answering questions (profiling, emotion detection, decisions affecting access to services), get advice, because other parts of the AI Act may apply._

**Sources:** [European Commission: Transparency obligations under Article 50](https://digital-strategy.ec.europa.eu/en/faqs/transparency-obligations-under-article-50-ai-act) · [Article 50, EU AI Act (full text)](https://artificialintelligenceact.eu/article/50/)
